export const pins = {
  mapView: {
    undoPinMove: "Undo pin move",
    redoPinMove: "Redo pin move",
    showAddPins: "Add pins",
    hideAddPins: "Hide add pins",
    dragInstruction: "Click and drag icons on to the map",
    editPin: "Edit pin",
    untitledPin: "Untitled pin",
    loadingPins: "Loading pins…",
    fittingPins: "Finding your pins…",
    longPressToMove: "Long-press a pin to unlock it, then drag to move. Tap a pin to edit.",
    gpsProximityWarning: "'{{pinA}}' and '{{pinB}}' are only {{metres}}m apart. In some environments GPS may not be accurate enough to distinguish between them.",
  },
  presentation: {
    present: "Present",
    exit: "Exit presentation",
    scanHint: "Scan to open",
    shareToggle: "Listening link",
    preparingQr: "Preparing QR…",
    qrUnavailable: "QR code unavailable",
    qrAlt: "QR code to open {{name}}",
    tapPin: "Tap a pin to hear it",
    playPreview: "Play preview",
    pausePreview: "Pause preview",
    preparingPreview: "Preparing preview…",
    previewUnavailable: "No project preview yet",
    nowPlaying: "Playing {{name}}",
    untitledPin: "Untitled pin",
    playPin: "Play {{name}}",
    playPinAt: "Play {{name}}, recording {{current}} of {{total}}",
    pausePin: "Pause {{name}}",
    noAudio: "{{name}} has no audio",
    clipPosition: "{{current}}/{{total}}",
    previousRecording: "Previous recording for {{name}}",
    nextRecording: "Next recording for {{name}}",
    shareHint: "Create a link for the people who should hear this project away from the pins.",
    shareCreate: "Create listening link",
    shareCreating: "Creating link…",
    shareCopy: "Copy listening link",
    shareCopied: "Listening link copied",
    shareCopyFailed: "Could not copy the listening link",
    shareStop: "Stop sharing",
    shareStopped: "Listening link turned off",
    shareEnabled: "Listening link is ready to share",
    shareCreateFailed: "Could not create the listening link",
    shareStopFailed: "Could not turn off sharing",
    shareDemo: "Listening links are unavailable in demo mode",
    shareScan: "Scan to listen",
    shareOpening: "Opening presentation…",
    shareUnavailable: "This listening link is no longer available.",
    listenersOpen: "Anyone with the link can listen. Add people to limit it to their Overhear accounts.",
    listenersRestricted: "Only these signed-in people can open the link.",
    listenersSearch: "Add a person",
    listenersSearching: "Searching…",
    listenersSaveFailed: "Could not update who can listen",
    listenSignIn: "Sign in to listen",
    listenDenied: "This account does not have access to these recordings.",
    listenEmail: "Email",
    listenPassword: "Password",
    listenSubmit: "Sign in",
    listenGoogle: "Continue with Google",
    listenFailed: "Could not sign in. Check the email and password, then try again.",
    loading: "Preparing presentation…",
    emptyPins: "This project has no pins to present yet.",
  },
  listView: {
    title: "Manage and organise all pins and their recordings for this project.",
    subtitle: "View, edit, and reorder pins",
    description: "Each pin can contain multiple recordings that listeners can discover and collect.",
    qrCode: {
      title: "Project QR Code",
      description: "Download a QR code for this project. When scanned, listeners will be taken directly to the project in the Overhear app, making it easy to share and promote your project.",
      demoTooltip: "In demo mode, this downloads the QR code for the 'Public Map' project instead, as demo projects don't have accessible QR codes."
    },
    projectIntro: {
      title: "Project Intro",
      empty: "Add a Project Intro recording, or drag an unassigned recording into the area below. It plays once before normal per-pin collection starts.",
      dropHint: "Release to set as the project intro.",
      addButton: "Add Project Intro"
    },
    engagement: {
      totalCollections: "Collections",
      totalPlays: "Plays",
      introCollections: "Collections",
      introPlays: "Plays",
      pinEngagementLabel: "Pin engagement",
      pinEngagementDescription: "Collections and plays across map pins only. Excludes the project intro recording.",
      projectEngagementLabel: "Project engagement",
      projectEngagementDescription: "Collections and plays across all pins in this project.",
      introEngagementLabel: "Intro only",
      introEngagementDescription: "Collections and plays for the project intro recording. Shown separately from pin totals above.",
      collectionsTooltip: "Times signed-in users saved recordings from this project to their library. Guest listeners cannot collect.",
      playsTooltip: "A play counts when a listener hears at least 90% of a recording, including guests who browse without an account.",
      playsTooltipExcludesIntro: "A play counts when a listener hears at least 90% of a recording on map pins, including guests. Excludes the project intro recording.",
      pinCollectionsTooltip: "Collections for this pin (signed-in users only).",
      pinPlaysTooltip: "{{count}} total plays for this pin. A play counts when a listener hears at least 90% of a recording (all listeners, including guests).",
      pinRowPlaysTooltip: "Total plays for this pin. A play counts when a listener hears at least 90% of a recording (all listeners, including guests).",
      recordingCollectionsTooltip: "Collections for this recording (signed-in users only).",
      recordingPlaysTooltip: "Plays for this recording. A play counts when a listener hears at least 90% of it (all listeners, including guests).",
      columnCollections: "Collections",
      monthlyReportDescription: "Receive monthly reports with recent additions, total collections, and total plays"
    },
    orphanDrop: {
      emptyPin: "Drop an unassigned recording here, or use Add Recording.",
      dropHint: "Release to assign to this pin."
    },
    collectionLogicHealth: {
      title: "Pin collection logic needs attention",
      summaryOne: "One pin still has non-regular collection logic.",
      summaryMany: "{{count}} pins still have non-regular collection logic.",
      description:
        "On regular (non-sequential, non-branching) projects these values can leave pins semi-transparent in the app. Reset collection logic to 0. Live / unavailable is left unchanged.",
      repair: "Reset to collection logic 0",
      repairing: "Resetting…",
      repairSuccess: "Reset collection logic on {{count}} pin(s).",
      repairNone: "No pin collection logic repairs were needed.",
      repairFailed: "Could not reset pin collection logic. Please try again or contact support.",
    },
    tabs: {
      pins: "Pins",
      responses: "Responses",
      submissions: "Submissions",
      tags: "Tags",
      forms: "Forms"
    },
    modes: {
      details: "Details & Ordering",
      transcripts: "Transcripts",
      previews: "Previews",
      ariaLabel: "Pins task mode",
      unassigned: "Unassigned",
      emptyTranscripts: "No recordings in this project yet.",
      emptyPreviews: "No recordings in this project yet.",
      filterAll: "All",
      filterNeedsTranscript: "Needs transcript",
      filterGenerating: "Generating",
      filterMissingPreview: "Missing preview",
      filterSavedPreview: "Saved",
      statusNeedsTranscript: "Needs transcript",
      statusGenerating: "Generating",
      statusReady: "Ready",
      statusMissingPreview: "Missing",
      statusSavedPreview: "Saved",
      expandTranscript: "Edit transcript",
      expandPreview: "Edit preview",
      goToTranscript: "Transcript",
      goToPreview: "Preview",
      collapse: "Collapse",
    },
    responses: {
      title: "User Responses",
      empty: "No responses yet",
      confirmDelete: "Are you sure you want to delete this response?",
      recording: "Recording",
      responseText: "Response Text",
      audioResponse: "Audio Response",
      flaggedPhrases: "Flagged Phrases",
      requiresModeration: "Requires Moderation",
      audio: "Audio",
      text: "Text",
      noDate: "No date available",
      inResponseTo: "In response to",
      responsesTo: "Responses to",
      demoExplanation: "User Responses allow listeners to provide feedback, comments, or audio responses to recordings. This feature can be enabled or disabled when setting up a project in the Project Editor. When enabled, users can respond to recordings with text or audio, and project administrators can review and moderate these responses here."
    },
    submissions: {
      title: "User Submissions",
      empty: "No submissions yet",
      setRating: "Set Content Rating",
      confirmDelete: "Are you sure you want to delete this submission?",
      selectRating: "Select Rating",
      demoExplanation: "User Submissions allow listeners to submit their own recordings to be included in the project. This feature can be enabled or disabled when setting up a project in the Project Editor. When enabled, users can submit recordings that project administrators can review, rate for content appropriateness, and approve or reject here."
    },
    notifications: {
      newResponse: "New response received",
      newSubmission: "New submission received"
    },
    transcription: {
      emptyIntro:
        "No transcription yet. You can generate one from the uploaded audio, or wait if processing has already started.",
      generateTranscript: "Generate transcript",
      generateTranscriptDialogTitle: "Generate transcription",
      generateTranscriptDialogIntro:
        "Speech-to-text runs on the stored audio. Optionally add transcript text below to align word timings to your wording.",
      sttOptionalTranscriptLabel: "Transcript text (optional)",
      sttOptionalTranscriptHint:
        "If you add text here, the service still runs speech-to-text on the stored audio for word timings, then aligns those timings to your wording. Leave blank for a fully automatic transcript.",
      appTranscriptionPreviewTitle: "Play app (mobile) preview",
      appTranscriptionPreviewHint:
        "Same strip as the Play screen: words appear in order while this recording’s audio plays (use the player above). Scrolls horizontally when the line gets long.",
      appTranscriptionPreviewIdle: "Press play on the recording to see words appear here in sync.",
      generatingTranscript: "Starting transcription…",
      transcriptionQueued:
        "Transcription has been requested. It may take a minute to appear; reopen the recording or refresh the list if needed.",
      regenerateTranscript: "Regenerate transcription",
      regenerateTranscriptTitle: "Regenerate transcription",
      regenerateTranscriptWarning:
        "This will replace the current transcript, word-level timings, alternatives, and any manual edits with a new automated transcription from the stored audio. This cannot be undone.",
      regenerateTranscriptConfirm: "Are you sure you want to continue?",
      regenerateTranscriptConfirmButton: "Yes, regenerate",
      speakersInRecording: "Speakers in this recording",
      speakerModeSingleLabel: "One speaker",
      speakerModeMultipleLabel: "Multiple speakers",
      speakerModeRegenerateHint:
        "Multiple speakers enables diarisation in transcription (may reduce word alternatives).",
      sttSuperAdminSection: "Speech-to-text (super admin)",
      sttProviderLabel: "Engine",
      sttProviderGoogleV1: "Google Speech-to-Text v1",
      sttProviderAssemblyAI: "AssemblyAI (word timings + diarisation)",
      sttPresetLabel: "Model preset",
      sttPresetGoogleOnlyNote: "Model preset applies to Google Speech-to-Text only (not AssemblyAI).",
      sttPresetBalanced: "Balanced (clip-length aware)",
      sttPresetLongForm: "Long-form priority",
      sttPresetShortClips: "Short clips priority",
      sttOverrideThisRun: "Override saved engine and preset for this run only",
      sttOverrideEngineLabel: "Engine for this run",
      sttOverridePresetLabel: "Preset for this run",
      editWordsHint:
        "Select a word to edit its text. Words with badges have alternative suggestions. Use the +/- buttons to add or remove words.",
      speakerEditModeLabel: "Speaker edit mode",
      speakerEditModeHint:
        "Click a word to select it. Shift+click another word to select the whole range. Choose a speaker tag, then apply to update diarisation for those words.",
      speakerAssignTagLabel: "Speaker tag",
      applySpeakerToSelection: "Apply to selection",
      speakerSelectionCount: "{{count}} word(s) selected",
      speakerTagOption: "Speaker {{tag}}"
    },
    preview: {
      title: "Preview audio",
      savedBadge: "saved",
      noAudio: "This recording has no audio to preview.",
      tooShort: "Preview requires at least 3 seconds of audio.",
      demoDisabled: "Saving preview audio is disabled in demo mode.",
      selection: "Selection {{start}}s – {{end}}s ({{duration}}s). Drag the handles to keep the clip between 3 and 10 seconds.",
      fadeIn: "Fade in ({{value}}s)",
      fadeOut: "Fade out ({{value}}s)",
      playSelection: "Play selection",
      playingSelection: "Playing…",
      save: "Save preview",
      saving: "Saving…",
      download: "Download preview",
      clear: "Clear preview",
      clearing: "Clearing…",
      savedPreviewLabel: "Saved preview",
      saved: "Preview audio saved.",
      cleared: "Preview audio cleared.",
      saveFailed: "Failed to save preview audio",
      clearFailed: "Failed to clear preview audio",
      downloadFailed: "Failed to download preview audio",
      projectDownload: "Download project preview",
      projectDownloading: "Downloading preview…",
      projectSave: "Save project preview",
      projectSaving: "Saving preview…",
      projectSaved: "Project preview saved for the app and website.",
      projectSaveFailed: "Failed to save project preview",
      projectCrossfade: "Crossfade between clips ({{value}}s)",
      projectEmpty: "No saved recording previews in this project yet.",
      projectDownloaded: "Project preview downloaded.",
      projectDownloadFailed: "Failed to build project preview",
      projectUnsavedFlag: "Recording previews are ready — save a project preview for the app and website.",
      projectUnsavedShort: "Unsaved — recording previews ready",
      outdatedBadge: "changes pending",
      projectOutdatedFlag: "Recording previews have changed — save the project preview again for the app and website.",
      projectOutdatedShort: "Changes pending — re-save project preview",
      projectSectionTitle: "Project preview",
      projectPlay: "Play preview",
      projectPlaying: "Preparing playback…",
      projectPlayFailed: "Failed to prepare project preview playback",
      projectUpload: "Upload custom preview",
      projectUploading: "Uploading preview…",
      projectUploaded: "Custom project preview uploaded.",
      projectUploadFailed: "Failed to upload project preview",
      projectClear: "Clear project preview",
      projectClearing: "Clearing…",
      projectCleared: "Project preview cleared.",
      projectClearFailed: "Failed to clear project preview",
      projectSavedLabel: "Current project preview",
      projectUploadHint: "Upload an MP3 to override the auto-stitched preview for the app and website."
    },
    recordingActions: {
      openMenu: "Open recording actions",
      editRecording: "Edit recording",
      moveTitle: "Move recording",
      moveDescription: "Choose the pin in this project that this recording should belong to.",
      targetPin: "Target pin",
      loadingPins: "Loading pins…",
      noTargetPins: "There are no other pins in this project to move this recording to.",
      selectTargetPin: "Please select a target pin",
      loadPinsFailed: "Failed to load pins for this project",
      moveSuccess: "Recording moved successfully",
      moveFailed: "Failed to move recording"
    }
  },
  pinTypes: {
    geoLocation: "Geo Location",
    qrCode: "QR Code",
    QREpisode: "QR Episode",
    wanderPin: "Wander Pin",
    collaborativeStem: "Collaborative Stem",
    bus: "Bus",
    episode: "Episode",
    location: "Location",
  },
  editor: {
    collectionDistance: "Collection Distance (metres)"
  },
  form: {
    editPin: "Edit Pin",
    createPin: "Create Pin",
    pinInformation: "Pin Information",
    selectUniqueIcon: "Select Unique Icon",
    designUniquePin: "Design unique pin",
    useProjectPin: "Use project pin"
  }
}
